// analytics/components/filters/UnifiedFilterComponents.jsx
// Reusable filter components for consistent behavior across tabs

import React from 'react';

/**
 * Individual filter components for reuse
 */

export const DateRangeFilter = ({ value, onChange, label = "Date Range", className = "col-md-3" }) => (
    <div className={className}>
        <label className="form-label small fw-bold">{label}</label>
        <select 
            className="form-select form-select-sm" 
            name="dateRange" 
            value={value} 
            onChange={(e) => onChange('dateRange', e.target.value)}
        >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
        </select>
    </div>
);

export const ProjectFilter = ({ value, onChange, projects = [], label = "Part", className = "col-md-3" }) => (
    <div className={className}>
        <label className="form-label small fw-bold">{label}</label>
        <select 
            className="form-select form-select-sm" 
            name="project" 
            value={value} 
            onChange={(e) => onChange('project', e.target.value)}
        >
            <option value="">All Parts</option>
            {projects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
    </div>
);

export const StationFilter = ({ value, onChange, stations = [], label = "Station", className = "col-md-3" }) => (
    <div className={className}>
        <label className="form-label small fw-bold">{label}</label>
        <select 
            className="form-select form-select-sm" 
            name="station" 
            value={value} 
            onChange={(e) => onChange('station', e.target.value)}
        >
            <option value="">All Stations</option>
            {stations.map(s => (
                <option key={s.station_id} value={s.station_id}>
                    {s.station_name || s.station_id}
                </option>
            ))}
        </select>
    </div>
);

export const LotIdFilter = ({ value, onChange, lotIds = [], label = "Lot ID", className = "col-md-3", show = true }) => {
    if (!show) return null;
    
    return (
        <div className={className}>
            <label className="form-label small fw-bold">{label}</label>
            <select 
                className="form-select form-select-sm" 
                name="lotId" 
                value={value} 
                onChange={(e) => onChange('lotId', e.target.value)}
            >
                <option value="">All Lot IDs</option>
                {lotIds.map(lot => <option key={lot} value={lot}>{lot}</option>)}
            </select>
        </div>
    );
};

export const SortByFilter = ({ value, onChange, options = [], label = "Sort By", className = "col-md-3" }) => (
    <div className={className}>
        <label className="form-label small fw-bold">{label}</label>
        <select 
            className="form-select form-select-sm" 
            name="sortBy" 
            value={value} 
            onChange={(e) => onChange('sortBy', e.target.value)}
        >
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
);

export const StatusFilter = ({ value, onChange, options = [], label = "Status Filter", className = "col-md-3" }) => (
    <div className={className}>
        <label className="form-label small fw-bold">{label}</label>
        <select 
            className="form-select form-select-sm" 
            name="statusFilter" 
            value={value} 
            onChange={(e) => onChange('statusFilter', e.target.value)}
        >
            <option value="">All</option>
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
);

/**
 * Unified Filter Panel for different tab types
 */
export const UnifiedFilterPanel = ({ 
    filters, 
    onFilterChange, 
    availableOptions = {}, 
    config = {},
    tabType = 'default',
    showConfigInfo = true
}) => {
    const { projects = [], stations = [], lotIds = [] } = availableOptions;
    const { includeManualImages, itemsPerPage } = config;

    // Define sort options per tab type
    const getSortOptions = () => {
        switch (tabType) {
            case 'projects':
                return [
                    { value: 'defect_rate', label: 'Defect Rate' },
                    { value: 'total_inspections', label: 'Total Inspections' },
                    { value: 'project_name', label: 'Part Name' },
                    { value: 'last_inspection', label: 'Last Inspection' }
                ];
            case 'lots':
                return [
                    { value: 'last_run_at', label: 'Most Recent' },
                    { value: 'defect_rate', label: 'Highest Defect Rate' },
                    { value: 'total_inspections', label: 'Most Inspections' }
                ];
            case 'sessions':
                return [
                    { value: 'start_time', label: 'Start Time' },
                    { value: 'highest_defect_rate', label: 'Highest Defect Rate' },
                    { value: 'lowest_defect_rate', label: 'Lowest Defect Rate' },
                    { value: 'total_inspections', label: 'Total Inspections' }
                ];
            case 'stations':
                return [
                    { value: 'defect_rate', label: 'Defect Rate' },
                    { value: 'total_inspections', label: 'Total Inspections' },
                    { value: 'station_name', label: 'Station Name' }
                ];
            default:
                return [
                    { value: 'last_run_at', label: 'Most Recent' },
                    { value: 'defect_rate', label: 'Defect Rate' }
                ];
        }
    };

    const getStatusOptions = () => {
        if (tabType === 'projects') {
            return [
                { value: 'active', label: 'Active Only' },
                { value: 'trained', label: 'Trained Only' },
                { value: 'high_defect', label: 'High Defect Rate (>5%)' }
            ];
        }
        return [];
    };

    const sortOptions = getSortOptions();
    const statusOptions = getStatusOptions();

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-filter text-primary me-2"></i>Filters
                    </h5>
                    {showConfigInfo && (
                        <small className="text-muted">
                            <i className="fas fa-cog me-1"></i>
                            Manual images: {includeManualImages ? 'included' : 'excluded'} •
                            Items per page: {itemsPerPage || 20}
                        </small>
                    )}
                </div>
            </div>
            <div className="card-body">
                <div className="row">
                    <DateRangeFilter 
                        value={filters.dateRange} 
                        onChange={onFilterChange} 
                    />
                    
                    {tabType !== 'stations' && tabType !== 'projects' && (
                        <ProjectFilter 
                            value={filters.project} 
                            onChange={onFilterChange} 
                            projects={projects} 
                        />
                    )}
                    
                    {(tabType === 'projects' || tabType === 'sessions') && (
                        <StationFilter 
                            value={filters.station} 
                            onChange={onFilterChange} 
                            stations={stations} 
                        />
                    )}
                    
                    <SortByFilter 
                        value={filters.sortBy} 
                        onChange={onFilterChange} 
                        options={sortOptions} 
                    />
                </div>

                {/* Second row for conditional filters */}
                <div className="row mt-3">
                    {/* Lot ID filter - show when project is selected (except for stations tab) */}
                    {tabType !== 'stations' && filters.project && (
                        <LotIdFilter 
                            value={filters.lotId} 
                            onChange={onFilterChange} 
                            lotIds={lotIds}
                            show={true}
                        />
                    )}
                    
                    {/* Status filter for projects tab */}
                    {tabType === 'projects' && statusOptions.length > 0 && (
                        <StatusFilter 
                            value={filters.statusFilter} 
                            onChange={onFilterChange} 
                            options={statusOptions} 
                        />
                    )}
                </div>
            </div>
        </div>
    );
};